export type SerializedMap<TKey, TValue> = {
  __type: "Map";
  value: Array<[TKey, TValue]>;
};
export type SerializedSet<TValue> = { __type: "Set"; value: Array<TValue> };

/**
 * Adds support for maps, introducing a metadata prop called __type
 * which described that it's a Map
 */
export function replacer(_key: any, value: any) {
  if (value instanceof Map) {
    return {
      __type: "Map",
      value: Array.from(value.entries()), // or with spread: value: [...value]
    };
  } else if (value instanceof Set) {
    return {
      __type: "Set",
      value: Array.from(value.values()),
    };
  } else {
    return value;
  }
}

/**
 * Deserializes the same thing with support for Set and Map.
 */
export function reviver(_key: any, value: any) {
  if (typeof value === "object" && value !== null && value.__type === "Map") {
    if (value.__type === "Map") {
      new Map(value.value);
    } else if (value.__type === "Set") {
      return new Set(value);
    }
  }
  return value;
}

export function isSerializedMap(o: any) {
  return (
    typeof o === "object" &&
    "__type" in o &&
    o.__type === "Map" &&
    Array.isArray(o.value)
  );
}

export function reviveMap<K, T>(o: SerializedMap<K, T>): Map<K, T> {
  if (isSerializedMap(o)) {
    return new Map(o.value);
  }

  throw new Error("Could not parse Map");
}

export function reviveSet<T>(o: SerializedSet<T>): Set<T> {
  if (o.__type === "Set" && Array.isArray(o.value)) {
    return new Set(o.value);
  }

  throw new Error("Could not parse Set");
}

export function parsify(s: string): any {
  return JSON.parse(s, reviver);
}

/**
 * Adds support for Maps and Set using a __type to signify hard to serialize stuff.
 */
export function jsonify(obj: any, settings?: { indent?: number }): string {
  return JSON.stringify(obj, replacer, settings?.indent);
}
export const stringify = jsonify;

/**
 * Deeply recurse through an object or array and make the object serializable.
 * Doesn't support custom classes
 * Everything should be a json object event maps, sets and dates to iso strings.
 *
 * This is an alternative to jsonify, when we don't need to revive maps using the __type construct.
 *
 * Also if __type: "Map" setup is not wished for, one can use this first to unfold the
 * maps and sets info objects and arrays.
 *
 * BEWARE: This does copy the object deeply.
 */
export function serializify(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(serializify);
  }
  let r = { ...obj };
  for (const [k, v] of Object.entries(r)) {
    if (v instanceof Date) {
      r[k] = v.toISOString();
    } else if (v instanceof Map) {
      r[k] = serializify(Object.fromEntries(v.entries()));
    } else if (v instanceof Set) {
      r[k] = Array.from(v).map(serializify);
    } else if (Array.isArray(v)) {
      r[k] = v.map(serializify);
    } else if (typeof v === "object") {
      r[k] = serializify(v);
    } else {
      r[k] = v;
    }
  }
  return r;
}

/**
 * Simple util that serializes and parses the obj to create a deep copy.
 * This is the lazy way of doing it.
 */
export function clone(obj: any) {
  return parsify(jsonify(obj));
}
